import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import QMSLayout from "../components/QMSLayout";
import { API, useAuth } from "../App";
import { useTranslation } from "react-i18next";
import {
  BookOpen, GraduationCap, Loader2,
  CheckCircle2, Globe, ExternalLink,
  Map, Eye, ShoppingBag, ArrowRight, Barcode, BookMarked,
  Download, Package, FileCheck, Lock
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════════
   BOOK COVER — Simple flat image display (trimmed, no white edges)
   All books use the same clean component with local assets
   ═══════════════════════════════════════════════════════════════════ */
const BookCover = ({ bookId, coverImage, altText, onClick }) => (
  <div
    onClick={onClick}
    className="relative cursor-pointer group w-full max-w-[280px] overflow-hidden transition-transform hover:scale-[1.02]"
    data-testid={`book-cover-${bookId}`}
    style={{
      aspectRatio: "2 / 3",
      boxShadow: "8px 4px 30px rgba(0,0,0,0.6), 2px 2px 10px rgba(0,0,0,0.3)",
    }}
  >
    <img src={coverImage} alt={altText} className="w-full h-full object-cover" />
    <div className="absolute inset-0 z-20 bg-white/0 group-hover:bg-white/5 transition-colors flex items-center justify-center">
      <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 px-4 py-2 rounded">
        <span className="text-white text-xs font-bold flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Open Preview</span>
      </div>
    </div>
    <div className="absolute left-0 top-0 bottom-0 w-[6px] z-30"
      style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.1) 40%, transparent 100%)" }} />
  </div>
);

const PublishingPortalPage = () => {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [activeTab, setActiveTab] = useState("bookshelf");
  const [books, setBooks] = useState([]);
  const [marketplaces, setMarketplaces] = useState({});
  const [publishingRoadmap, setPublishingRoadmap] = useState([]);
  const [courses, setCourses] = useState([]);
  const [allIsbns, setAllIsbns] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, courseRes] = await Promise.all([
        fetch(`${API}/publishing/catalog`, { credentials: "include" }),
        fetch(`${API}/publishing/courses`, { credentials: "include" }),
      ]);
      if (catRes.ok) {
        const d = await catRes.json();
        setBooks(d.books || []);
        setMarketplaces(d.marketplaces || {});
        setPublishingRoadmap(d.publishing_roadmap || []);
        setAllIsbns(d.all_isbns || []);
      }
      if (courseRes.ok) setCourses((await courseRes.json()).courses || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const completeModule = async (courseId, moduleNum) => {
    try {
      await fetch(`${API}/publishing/courses/${courseId}/complete-module`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ module_num: moduleNum }),
      });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const [bundleStatus, setBundleStatus] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [editorialScore, setEditorialScore] = useState(null);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [rescoring, setRescoring] = useState(false);
  const [coverManifest, setCoverManifest] = useState(null);
  // Submission panel state
  const [submSeries, setSubmSeries] = useState("III");
  const [submEdition, setSubmEdition] = useState("hardcover_en");
  const [submDistributor, setSubmDistributor] = useState("amazon_kdp");
  const [submPreview, setSubmPreview] = useState(null);
  const [submGenerating, setSubmGenerating] = useState(false);
  const [submResult, setSubmResult] = useState(null);
  const [submHistory, setSubmHistory] = useState([]);
  const [copiedField, setCopiedField] = useState("");
  // Distribution tracker state
  const [distSummary, setDistSummary] = useState(null);
  const [liveCatalog, setLiveCatalog] = useState(null);
  const [showLiveCatalog, setShowLiveCatalog] = useState(false);
  const [markRow, setMarkRow] = useState(null); // the row being edited
  const [markForm, setMarkForm] = useState({ distribution_status: "submitted", distributor_product_id: "", distributor_product_url: "", date: "", notes: "" });
  const [markSaving, setMarkSaving] = useState(false);

  const loadDistSummary = () => {
    fetch(`${API}/publishing/bundle/distribution-summary`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => setDistSummary(d))
      .catch(() => {});
  };
  const loadLiveCatalog = () => {
    fetch(`${API}/publishing/bundle/live-catalog`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => setLiveCatalog(d))
      .catch(() => {});
  };

  // ESC closes any open modal
  useEffect(() => {
    if (!showLiveCatalog && !markRow) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (showLiveCatalog) setShowLiveCatalog(false);
        if (markRow) setMarkRow(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showLiveCatalog, markRow]);

  const loadSubmHistory = () => {
    fetch(`${API}/publishing/bundle/submission-package/history?limit=10`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => setSubmHistory(d?.items || []))
      .catch(() => {});
  };

  const loadSubmPreview = (series, edition, distributor) => {
    fetch(`${API}/publishing/bundle/submission-package/metadata-preview?series=${series}&edition=${edition}&distributor=${distributor}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => setSubmPreview(d))
      .catch(() => {});
  };

  const loadScoreHistory = () => {
    fetch(`${API}/publishing/bundle/editorial-score/history?limit=10`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => setScoreHistory(d?.items || []))
      .catch(() => {});
  };

  useEffect(() => {
    if (isAdmin) {
      fetch(`${API}/publishing/bundle/status`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then(d => setBundleStatus(d))
        .catch(() => {});
      fetch(`${API}/publishing/bundle/editorial-score`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then(d => setEditorialScore(d))
        .catch(() => {});
      loadScoreHistory();
      fetch(`${API}/publishing/covers/manifest`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then(d => setCoverManifest(d))
        .catch(() => {});
      loadSubmHistory();
      loadSubmPreview("III", "hardcover_en", "amazon_kdp");
      loadDistSummary();
      loadLiveCatalog();
    }
  }, [isAdmin]);

  const openMarkModal = (row) => {
    setMarkRow(row);
    setMarkForm({
      distribution_status: row.distribution_status || "submitted",
      distributor_product_id: row.distributor_product_id || "",
      distributor_product_url: row.distributor_product_url || "",
      date: new Date().toISOString().slice(0, 10),
      notes: row.notes || "",
    });
  };

  const saveMarkStatus = async () => {
    if (!markRow) return;
    setMarkSaving(true);
    try {
      const payload = {
        distribution_status: markForm.distribution_status,
        distributor_product_id: markForm.distributor_product_id || "",
        distributor_product_url: markForm.distributor_product_url || "",
        notes: markForm.notes || "",
      };
      if (markForm.date) {
        const iso = new Date(markForm.date + "T00:00:00Z").toISOString();
        if (markForm.distribution_status === "submitted") payload.submitted_at = iso;
        if (markForm.distribution_status === "live") payload.went_live_at = iso;
      }
      const r = await fetch(`${API}/publishing/bundle/submission-package/${markRow.submission_id}/status`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        setMarkRow(null);
        loadSubmHistory();
        loadDistSummary();
        loadLiveCatalog();
      }
    } finally {
      setMarkSaving(false);
    }
  };

  const regenerateSubmPreview = (nextSeries, nextEdition, nextDistributor) => {
    loadSubmPreview(nextSeries, nextEdition, nextDistributor);
    setSubmResult(null);
  };

  const copyToClipboard = (value, fieldKey) => {
    const text = String(value || "");
    const markCopied = () => {
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(""), 1200);
    };
    // Preferred modern API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(markCopied).catch(() => fallbackCopy(text, markCopied));
      return;
    }
    fallbackCopy(text, markCopied);
  };
  const fallbackCopy = (text, onDone) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      onDone();
    } catch { /* no-op */ }
  };

  const generateSubmission = async () => {
    setSubmGenerating(true);
    setSubmResult(null);
    try {
      const r = await fetch(`${API}/publishing/bundle/submission-package`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          series: submSeries,
          edition: submEdition,
          distributor: submDistributor,
          confirm: true,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        setSubmResult({ error: err.detail || `HTTP ${r.status}` });
        return;
      }
      const blob = await r.blob();
      const sizeBytes = blob.size;
      const zipHash = r.headers.get("X-Zip-Hash") || "";
      const wordCount = r.headers.get("X-Word-Count") || "";
      const pageCount = r.headers.get("X-Page-Count") || "";
      const isbn = r.headers.get("X-ISBN") || "";
      const disposition = r.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `submission_${submSeries}_${submEdition}_${submDistributor}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setSubmResult({
        filename,
        sizeBytes,
        zipHash,
        wordCount,
        pageCount,
        isbn,
      });
      loadSubmHistory();
    } finally {
      setSubmGenerating(false);
    }
  };

  const refreshEditorialScore = async () => {
    setRescoring(true);
    try {
      const r = await fetch(`${API}/publishing/bundle/editorial-score?refresh=true`, { credentials: "include" });
      if (r.ok) setEditorialScore(await r.json());
      loadScoreHistory();
    } finally {
      setRescoring(false);
    }
  };

  const allTabs = [
    { id: "bookshelf", label: "Bookshelf", icon: BookOpen, adminOnly: false },
    { id: "isbn-registry", label: "ISBN Registry", icon: Barcode, adminOnly: true },
    { id: "publisher-package", label: "Publisher Package", icon: Package, adminOnly: true },
    { id: "courses", label: "Courses", icon: GraduationCap, adminOnly: false },
    { id: "where-to-buy", label: "Where to Buy", icon: ShoppingBag, adminOnly: false },
  ];
  const tabs = allTabs.filter(tab => !tab.adminOnly || isAdmin);

  if (loading) return <QMSLayout title={t('platform:publishing.title')}><div className="flex justify-center h-64 items-center"><Loader2 className="w-8 h-8 animate-spin text-[#00C8E0]" /></div></QMSLayout>;

  return (
    <QMSLayout title={t('platform:publishing.title')}>
      <div className="space-y-6" data-testid="publishing-page">
        {/* Header */}
        <div className="text-center">
          <div className="text-[10px] text-[#00C8E0] uppercase tracking-[4px] mb-2">Charles Muchina Presents</div>
          <h1 className="text-3xl font-bold text-white tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }} data-testid="publishing-title">
            Brain Drain Series
          </h1>
          <p className="text-slate-400 text-sm mt-2 max-w-xl mx-auto">
            I — Professional Engineering Reference Guides | II — Key Formula Models | III — Applied STEM Leadership for High-Reliability Engineering. Free 2-page preview per chapter.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#0A1628] p-1 rounded-[3px] border border-[#1E3A5F] overflow-x-auto justify-center">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`pub-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-2 rounded-[3px] text-xs font-bold uppercase tracking-wide transition-all whitespace-nowrap ${activeTab === t.id ? "bg-[#1A6BCC] text-white" : "text-slate-400 hover:text-white"}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>

        {/* BOOKSHELF TAB */}
        {activeTab === "bookshelf" && (
          <div className="space-y-8">
            {/* Book covers */}
            <div className="flex flex-col md:flex-row items-center md:items-start justify-center gap-8 md:gap-12 py-4 flex-wrap" data-testid="bookshelf">
              {books.map((book) => (
                <div key={book.book_id} className="flex flex-col items-center gap-4 max-w-[280px]">
                  {/* Cover — clean flat image, no barcodes or ISBNs */}
                  <BookCover
                    bookId={book.book_id}
                    coverImage={book.cover_image}
                    altText={book.title}
                    onClick={() => navigate(`/reader/${book.book_id}`)}
                  />

                  {/* Book info below cover */}
                  <div className="text-center space-y-1.5">
                    <h3 className="text-white font-bold text-sm leading-snug" data-testid={`book-title-${book.book_id}`}>
                      {book.title}
                    </h3>
                    <p className="text-[#00C8E0] text-[10px] italic">{book.subtitle}</p>
                    <p className="text-slate-500 text-xs">by {book.author}</p>
                    <p className="text-slate-500 text-xs">{book.chapter_count} Chapters</p>

                    {/* ISBN info (separate from the cover — shown as metadata below) */}
                    <div className="mt-2 bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px] px-3 py-2 inline-block" data-testid={`isbn-info-${book.book_id}`}>
                      <div className="text-[9px] text-slate-400 font-mono">ISBN {book.isbn}</div>
                      {book.isbn_editions && Object.keys(book.isbn_editions).length > 1 && (
                        <div className="text-[8px] text-slate-500 mt-0.5">
                          {Object.keys(book.isbn_editions).length} editions assigned
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => navigate(`/reader/${book.book_id}`)}
                      data-testid={`preview-${book.book_id}`}
                      className="inline-flex items-center gap-1.5 mt-2 px-5 py-2 bg-[#1A6BCC] hover:bg-[#2178DD] text-white text-xs font-bold uppercase rounded-[3px] transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" /> Read Preview
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Purchase banner */}
            <div className="bg-gradient-to-r from-[#132340] to-[#1A3055] border border-[#1E3A5F] rounded-[3px] p-6 text-center" data-testid="purchase-cta-banner">
              <ShoppingBag className="w-7 h-7 text-[#00C8E0] mx-auto mb-2" />
              <h3 className="text-white font-bold text-sm mb-1">Full Books Coming to Major Retailers</h3>
              <p className="text-slate-400 text-xs mb-3 max-w-md mx-auto">
                ISBNs assigned. Purchase the complete e-Book or Hardcover editions from Amazon Kindle, Apple Books, Barnes & Noble, and other major retailers.
              </p>
              <button onClick={() => setActiveTab("where-to-buy")} data-testid="go-to-where-to-buy"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#132340] border border-[#1A6BCC] hover:bg-[#1A6BCC] text-white text-xs font-bold uppercase rounded-[3px] transition-colors">
                <Globe className="w-3.5 h-3.5" /> See Where to Buy <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}

        {/* ISBN REGISTRY TAB */}
        {activeTab === "isbn-registry" && (
          <div className="space-y-6" data-testid="isbn-registry-tab">
            <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6">
              <div className="flex items-center gap-2 mb-1">
                <Barcode className="w-5 h-5 text-[#00C8E0]" />
                <h2 className="text-white font-bold text-sm">Global Publishing Network — ISBN Registry</h2>
              </div>
              <p className="text-slate-400 text-xs mb-1">Publisher: CHARSTAN Intelligence Platform</p>
              <p className="text-slate-500 text-[10px] mb-5">10 ISBNs assigned via Global Publishing Network (globalpublishingnetwork.com)</p>

              <div className="space-y-3">
                {allIsbns.filter(i => i.book_id !== "RESERVED").map((entry) => (
                  <div key={entry.isbn} className="bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px] p-4 flex items-center gap-4" data-testid={`isbn-entry-${entry.isbn}`}>
                    <img
                      src={`/assets/barcodes/Barcode_${entry.isbn}.jpg`}
                      alt={`Barcode ${entry.isbn}`}
                      className="h-14 object-contain bg-white rounded px-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-bold">{entry.title}</div>
                      {entry.subtitle && <div className="text-slate-400 text-[10px] mt-0.5">{entry.subtitle}</div>}
                      <div className="text-slate-400 text-xs mt-0.5">{entry.format} — {entry.language}</div>
                      <div className="text-[#00C8E0] font-mono text-xs mt-1">ISBN {entry.isbn}</div>
                    </div>
                    <div className="text-emerald-400 shrink-0">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                  </div>
                ))}

                {/* Reserved ISBN */}
                {allIsbns.filter(i => i.book_id === "RESERVED").map((entry) => (
                  <div key={entry.isbn} className="bg-[#0F1E35] border border-[#1E3A5F]/50 rounded-[3px] p-4 flex items-center gap-4 opacity-60" data-testid={`isbn-entry-${entry.isbn}`}>
                    <img
                      src={`/assets/barcodes/Barcode_${entry.isbn}.jpg`}
                      alt={`Barcode ${entry.isbn}`}
                      className="h-14 object-contain bg-white rounded px-1 opacity-50"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-300 text-sm font-bold">Reserved for Future Volume</div>
                      <div className="text-slate-500 text-xs mt-0.5">Format & Language TBD</div>
                      <div className="text-slate-500 font-mono text-xs mt-1">ISBN {entry.isbn}</div>
                    </div>
                    <div className="text-amber-400 shrink-0">
                      <BookMarked className="w-4 h-4" />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 bg-[#0A1628] border border-[#1E3A5F] rounded-[3px] p-4 text-xs text-slate-400">
                <div className="font-bold text-slate-300 mb-1">ISBN Assignment Summary</div>
                <div className="grid grid-cols-3 gap-2 text-[10px]">
                  <div><span className="text-[#00C8E0] font-bold">6</span> Hardcover editions (EN + PT-BR)</div>
                  <div><span className="text-[#00C8E0] font-bold">3</span> Bilingual eBooks (EN/PT-BR)</div>
                  <div><span className="text-amber-400 font-bold">1</span> Reserved for future use</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PUBLISHER PACKAGE TAB */}
        {activeTab === "publisher-package" && (
          <div className="space-y-6" data-testid="publisher-package-tab">
            {/* Distribution Dashboard */}
            {distSummary && (
              <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="distribution-dashboard">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="text-white font-bold text-sm">Distribution Dashboard</h2>
                    <p className="text-slate-400 text-xs mt-0.5">Live book catalog — single source of truth for where each edition is available.</p>
                  </div>
                  <button
                    onClick={() => setShowLiveCatalog(true)}
                    className="text-[10px] px-3 py-1.5 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 rounded-[3px] transition-colors font-bold"
                    data-testid="view-live-catalog-btn"
                  >
                    View live catalog ({distSummary.totals.live || 0})
                  </button>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="distribution-summary-rows">
                  {["amazon_kdp", "ingramspark", "spines", "wiley"].map((d) => {
                    const counts = distSummary.by_distributor[d] || {};
                    return (
                      <div key={d} className="bg-[#0A1628] border border-[#1E3A5F]/60 rounded-[3px] p-2.5" data-testid={`dist-card-${d}`}>
                        <div className="text-[10px] uppercase tracking-wider text-slate-300 font-bold mb-1.5">{d.replace(/_/g, " ")}</div>
                        <div className="flex items-center gap-3 text-[10px]">
                          <span className="text-emerald-400"><span className="font-bold text-sm">{counts.live || 0}</span> live</span>
                          <span className="text-sky-400"><span className="font-bold text-sm">{counts.submitted || 0}</span> submitted</span>
                          <span className="text-red-400"><span className="font-bold text-sm">{counts.rejected || 0}</span> rejected</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Live Catalog Modal */}
            {showLiveCatalog && (
              <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setShowLiveCatalog(false)} data-testid="live-catalog-modal">
                <div className="bg-[#0A1628] border border-[#1E3A5F] rounded-[3px] p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-bold text-sm">Live Catalog — Where the books are available</h3>
                    <div className="flex items-center gap-2">
                      {liveCatalog && liveCatalog.items.length > 0 && (
                        <a
                          href={`${API}/publishing/bundle/live-catalog/marketing-sheet`}
                          className="text-[10px] px-3 py-1.5 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 rounded-[3px] transition-colors font-bold"
                          data-testid="download-marketing-sheet-btn"
                        >
                          Download &ldquo;Where to Buy&rdquo; PDF
                        </a>
                      )}
                      <button onClick={() => setShowLiveCatalog(false)} className="text-slate-400 hover:text-white text-lg">×</button>
                    </div>
                  </div>
                  {liveCatalog && liveCatalog.items.length === 0 ? (
                    <div className="text-xs text-slate-400">No editions are live yet. Mark a submission as &quot;live&quot; once it&apos;s confirmed on the distributor platform.</div>
                  ) : liveCatalog ? (
                    <div className="space-y-2">
                      {liveCatalog.items.map((it) => (
                        <div key={it.submission_id} className="bg-[#132340] border border-emerald-500/20 rounded-[3px] p-3" data-testid={`live-row-${it.submission_id}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-white text-xs font-bold">Series {it.series} · {it.edition} · {it.distributor}</div>
                              <div className="text-[10px] text-slate-400">ISBN {it.isbn} · Went live {it.went_live_at ? new Date(it.went_live_at).toLocaleDateString() : "—"}</div>
                              {it.notes && <div className="text-[10px] text-slate-500 mt-0.5 italic">{it.notes}</div>}
                            </div>
                            {it.distributor_product_url && (
                              <a href={it.distributor_product_url} target="_blank" rel="noopener noreferrer" className="text-[10px] px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 rounded-[2px] font-bold transition-colors">
                                Open →
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <div className="text-slate-400 text-xs">Loading…</div>}
                </div>
              </div>
            )}

            {/* Mark-as Modal */}
            {markRow && (
              <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setMarkRow(null)} data-testid="mark-modal">
                <div className="bg-[#0A1628] border border-[#1E3A5F] rounded-[3px] p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-bold text-sm">Update distribution status</h3>
                    <button onClick={() => setMarkRow(null)} className="text-slate-400 hover:text-white text-lg">×</button>
                  </div>
                  <div className="text-[10px] text-slate-400 mb-3">
                    {markRow.submission_id} · Series {markRow.series} · {markRow.edition} · {markRow.distributor} · ISBN {markRow.isbn}
                  </div>
                  <div className="space-y-2.5">
                    <label className="block">
                      <span className="text-[10px] text-slate-400 uppercase tracking-wider">Status</span>
                      <select
                        value={markForm.distribution_status}
                        onChange={(e) => setMarkForm({ ...markForm, distribution_status: e.target.value })}
                        className="w-full mt-1 bg-[#132340] border border-[#1E3A5F] text-white text-xs rounded-[3px] px-2 py-1.5 focus:outline-none focus:border-cyan-400"
                        data-testid="mark-status-select"
                      >
                        <option value="generated">Generated</option>
                        <option value="submitted">Submitted</option>
                        <option value="live">Live</option>
                        <option value="rejected">Rejected</option>
                        <option value="withdrawn">Withdrawn</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-[10px] text-slate-400 uppercase tracking-wider">Product ID (ASIN for KDP, SKU for IngramSpark)</span>
                      <input
                        type="text"
                        value={markForm.distributor_product_id}
                        onChange={(e) => setMarkForm({ ...markForm, distributor_product_id: e.target.value })}
                        placeholder="e.g. B0D5XYZ123"
                        className="w-full mt-1 bg-[#132340] border border-[#1E3A5F] text-white text-xs rounded-[3px] px-2 py-1.5 focus:outline-none focus:border-cyan-400"
                        data-testid="mark-product-id-input"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px] text-slate-400 uppercase tracking-wider">Product URL</span>
                      <input
                        type="url"
                        value={markForm.distributor_product_url}
                        onChange={(e) => setMarkForm({ ...markForm, distributor_product_url: e.target.value })}
                        placeholder="https://amazon.com/dp/…"
                        className="w-full mt-1 bg-[#132340] border border-[#1E3A5F] text-white text-xs rounded-[3px] px-2 py-1.5 focus:outline-none focus:border-cyan-400"
                        data-testid="mark-product-url-input"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px] text-slate-400 uppercase tracking-wider">Date (applies to submitted/live)</span>
                      <input
                        type="date"
                        value={markForm.date}
                        onChange={(e) => setMarkForm({ ...markForm, date: e.target.value })}
                        className="w-full mt-1 bg-[#132340] border border-[#1E3A5F] text-white text-xs rounded-[3px] px-2 py-1.5 focus:outline-none focus:border-cyan-400"
                        data-testid="mark-date-input"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px] text-slate-400 uppercase tracking-wider">Notes</span>
                      <textarea
                        value={markForm.notes}
                        onChange={(e) => setMarkForm({ ...markForm, notes: e.target.value })}
                        placeholder="e.g. Rejection reason, launch notes, etc."
                        rows={3}
                        className="w-full mt-1 bg-[#132340] border border-[#1E3A5F] text-white text-xs rounded-[3px] px-2 py-1.5 focus:outline-none focus:border-cyan-400"
                        data-testid="mark-notes-input"
                      />
                    </label>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <button onClick={() => setMarkRow(null)} className="px-3 py-1.5 text-[10px] text-slate-300 border border-[#1E3A5F] hover:border-slate-400 rounded-[3px]">Cancel</button>
                    <button
                      onClick={saveMarkStatus}
                      disabled={markSaving}
                      className="px-4 py-1.5 bg-[#1A6BCC] hover:bg-[#2178DD] text-white text-[10px] font-bold rounded-[3px] disabled:opacity-50"
                      data-testid="mark-save-btn"
                    >
                      {markSaving ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Bundle Readiness Status */}
            {bundleStatus && (
              <div className={`rounded-[3px] p-4 border ${bundleStatus.bundle_ready ? "bg-[rgba(16,185,129,0.06)] border-emerald-500/20" : "bg-[rgba(251,191,36,0.06)] border-amber-500/20"}`} data-testid="bundle-readiness-banner">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {bundleStatus.bundle_ready ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <Lock className="w-5 h-5 text-amber-400" />}
                    <div>
                      <div className={`text-sm font-bold ${bundleStatus.bundle_ready ? "text-emerald-400" : "text-amber-400"}`}>
                        Bundle Readiness: {bundleStatus.completeness_pct}%
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {bundleStatus.total_chapters} chapters | {bundleStatus.with_enrichment} enriched | {bundleStatus.with_fme} with FME
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {Object.entries(bundleStatus.books || {}).map(([bid, count]) => (
                      <div key={bid} className="text-center">
                        <div className="text-white text-xs font-bold">{count}</div>
                        <div className="text-[9px] text-slate-500">{bid.replace("BOOK-", "Vol ")}</div>
                      </div>
                    ))}
                  </div>
                </div>
                {bundleStatus.bundle_ready && (
                  <div className="mt-2 h-1.5 bg-[#1E3A5F] rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-400 rounded-full transition-all" style={{ width: `${bundleStatus.completeness_pct}%` }} />
                  </div>
                )}
              </div>
            )}

            {/* Editorial Score Badge */}
            {editorialScore && (() => {
              const tier = editorialScore.tier || (editorialScore.overall_score >= 91 ? "green" : editorialScore.overall_score >= 80 ? "amber" : "red");
              const colour = tier === "green"
                ? { bg: "bg-[rgba(16,185,129,0.08)]", border: "border-emerald-500/30", text: "text-emerald-400", dot: "bg-emerald-400" }
                : tier === "amber"
                ? { bg: "bg-[rgba(251,191,36,0.08)]", border: "border-amber-500/30", text: "text-amber-400", dot: "bg-amber-400" }
                : { bg: "bg-[rgba(239,68,68,0.08)]", border: "border-red-500/30", text: "text-red-400", dot: "bg-red-400" };
              const s = editorialScore.summary || {};
              return (
                <div className={`rounded-[3px] p-4 border ${colour.bg} ${colour.border}`} data-testid="editorial-score-badge">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex w-2.5 h-2.5 rounded-full ${colour.dot}`} />
                      <div>
                        <div className={`text-sm font-bold ${colour.text}`} data-testid="editorial-score-value">
                          Editorial Score: {editorialScore.overall_score}/100
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          Automated scanner replica · {editorialScore.version || "—"} · {editorialScore.source === "cached" ? "cached" : "fresh"}
                          {editorialScore.computed_at ? ` · ${new Date(editorialScore.computed_at).toLocaleString()}` : ""}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Sparkline — last up to 10 builds, oldest → newest */}
                      {scoreHistory.length >= 2 && (() => {
                        const recent = [...scoreHistory].reverse();
                        const pts = recent.map(x => Number(x.overall_score) || 0);
                        const envs = recent.map(x => x.environment || "production");
                        const W = 160, H = 32, P = 2;
                        const minV = 60, maxV = 100;
                        const range = maxV - minV;
                        const step = pts.length > 1 ? (W - 2 * P) / (pts.length - 1) : 0;
                        const y = (v) => H - P - ((Math.max(minV, Math.min(maxV, v)) - minV) / range) * (H - 2 * P);
                        const line = pts.map((v, i) => `${i === 0 ? "M" : "L"}${P + i * step},${y(v)}`).join(" ");
                        const last = pts[pts.length - 1];
                        const stroke = last >= 91 ? "#34d399" : last >= 80 ? "#fbbf24" : "#f87171";
                        const delta = pts.length >= 2 ? (pts[pts.length - 1] - pts[pts.length - 2]).toFixed(1) : "0";
                        const dotFill = (env) => env === "production" ? stroke : env === "beta" ? "#fbbf24" : "#60a5fa";
                        const dotStroke = (env) => env === "production" ? "transparent" : "#0A1628";
                        const envsInView = Array.from(new Set(envs));
                        return (
                          <div className="flex items-center gap-2" data-testid="editorial-score-sparkline" title={`Last ${pts.length} builds: ${pts.map((v,i)=>`${v.toFixed(1)} [${envs[i]}]`).join(" → ")}`}>
                            <svg width={W} height={H} className="shrink-0">
                              <line x1={P} y1={y(91)} x2={W - P} y2={y(91)} stroke="#1E3A5F" strokeDasharray="2,2" strokeWidth="1" />
                              <path d={line} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                              {pts.map((v, i) => (
                                <circle
                                  key={i}
                                  cx={P + i * step}
                                  cy={y(v)}
                                  r={i === pts.length - 1 ? 2.6 : envs[i] === "production" ? 1.4 : 2.2}
                                  fill={dotFill(envs[i])}
                                  stroke={dotStroke(envs[i])}
                                  strokeWidth={envs[i] === "production" ? 0 : 1}
                                />
                              ))}
                            </svg>
                            <div className="text-[9px] text-slate-500 leading-tight">
                              <div className="uppercase tracking-wider" data-testid="sparkline-env-legend">
                                {envsInView.length === 1 ? envsInView[0].slice(0, 4) : "mixed"}
                              </div>
                              <div className={Number(delta) < 0 ? "text-red-400" : Number(delta) > 0 ? "text-emerald-400" : "text-slate-500"}>
                                {Number(delta) > 0 ? "+" : ""}{delta}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      <button
                        onClick={refreshEditorialScore}
                        disabled={rescoring}
                        className="text-[10px] px-3 py-1.5 border border-slate-600 hover:border-cyan-400 text-slate-300 hover:text-cyan-400 rounded-[3px] transition-colors disabled:opacity-50"
                        data-testid="refresh-editorial-score-btn"
                      >
                        {rescoring ? "Re-scoring..." : "Re-run scanner"}
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-[10px]" data-testid="editorial-score-breakdown">
                    {[
                      ["Word count ≥3,500", s.word_count_pass_pct],
                      ["No placeholders", s.no_placeholders_pct],
                      ["No code fences", s.no_code_fences_pct],
                      ["Acronyms defined", s.no_bare_acronyms_pct],
                      ["IEEE equations", s.has_ieee_equations_pct],
                      ["Tables referenced", s.has_table_refs_pct],
                    ].map(([label, val]) => (
                      <div key={label} className="bg-[#0A1628] border border-[#1E3A5F]/60 rounded-[3px] p-2">
                        <div className="text-slate-500 uppercase tracking-wider text-[9px]">{label}</div>
                        <div className={`font-bold mt-0.5 ${(val ?? 0) === 100 ? "text-emerald-400" : (val ?? 0) >= 90 ? "text-amber-400" : "text-red-400"}`}>
                          {val == null ? "—" : `${val}%`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Book Cover Downloads */}
            {coverManifest && (
              <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6" data-testid="book-covers-panel">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-white font-bold text-sm">Book Covers</h2>
                    <p className="text-slate-400 text-xs mt-1">
                      Distributor-ready cover files for Amazon KDP, IngramSpark, and web previews. High-res sources regenerated on demand.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="book-covers-grid">
                {coverManifest.items.map((item) => {
                    const slug = item.book_id === "BOOK-I" ? "book_I" : item.book_id === "BOOK-II" ? "book_II" : "book_III";
                    return (
                      <div key={item.book_id} className="bg-[#0A1628] border border-[#1E3A5F]/60 rounded-[3px] p-3" data-testid={`cover-card-${item.book_id}`}>
                        <div className="flex gap-3 mb-3">
                          <div className="w-16 h-24 bg-black/40 rounded-[2px] overflow-hidden shrink-0 border border-[#1E3A5F]">
                            <img
                              src={`/assets/covers/${slug}_cover.png`}
                              alt={`Cover ${item.series_number}`}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = "none"; }}
                            />
                          </div>
                          <div className="min-w-0">
                            <div className="text-white text-[11px] font-bold uppercase tracking-wider">Series {item.series_number}</div>
                            <div className="text-slate-400 text-[10px] mt-1 line-clamp-3">
                              {item.title.replace("Brain Drain Series - " + item.series_number + " - ", "")}
                            </div>
                            <div className="text-[9px] text-slate-500 mt-1 truncate">
                              ISBN-EN: {item.isbn_editions?.en_hardcover?.isbn || "—"}
                            </div>
                          </div>
                        </div>
                        <div className="space-y-1">
                          {item.available_formats.map((f) => (
                            <a
                              key={f.format}
                              href={`${API}/publishing/covers/${item.book_id}/${f.format}`}
                              className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-[#132340] hover:bg-[#1A6BCC]/20 border border-[#1E3A5F]/50 hover:border-cyan-400/40 text-slate-300 hover:text-cyan-400 text-[10px] rounded-[2px] transition-colors"
                              data-testid={`download-cover-${item.book_id}-${f.format}`}
                            >
                              <span className="font-bold uppercase tracking-wider">{f.format.replace(/_/g, ' ')}</span>
                              <span className="text-slate-500 text-[9px]">.{f.ext}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Generate Publisher Submission */}
            <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6" data-testid="submission-panel">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-white font-bold text-sm">Generate Publisher Submission</h2>
                  <p className="text-slate-400 text-xs mt-1">
                    One-click distributor-ready ZIP. Applies final publisher polish (paren stripping) + generates metadata, ONIX 3.0, covers, and distributor checklist.
                  </p>
                </div>
              </div>

              {/* Selectors */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="submission-selectors">
                <label className="block">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider">Series</span>
                  <select
                    value={submSeries}
                    onChange={(e) => { setSubmSeries(e.target.value); regenerateSubmPreview(e.target.value, submEdition, submDistributor); }}
                    className="w-full mt-1 bg-[#0A1628] border border-[#1E3A5F] text-white text-xs rounded-[3px] px-2 py-1.5 focus:outline-none focus:border-cyan-400"
                    data-testid="submission-series-select"
                  >
                    <option value="I">I</option>
                    <option value="II">II</option>
                    <option value="III">III</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider">Edition</span>
                  <select
                    value={submEdition}
                    onChange={(e) => { setSubmEdition(e.target.value); regenerateSubmPreview(submSeries, e.target.value, submDistributor); }}
                    className="w-full mt-1 bg-[#0A1628] border border-[#1E3A5F] text-white text-xs rounded-[3px] px-2 py-1.5 focus:outline-none focus:border-cyan-400"
                    data-testid="submission-edition-select"
                  >
                    <option value="hardcover_en">Hardcover EN</option>
                    <option value="hardcover_pt_br">Hardcover PT-BR</option>
                    <option value="ebook_bilingual">Bilingual eBook</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider">Distributor</span>
                  <select
                    value={submDistributor}
                    onChange={(e) => { setSubmDistributor(e.target.value); regenerateSubmPreview(submSeries, submEdition, e.target.value); }}
                    className="w-full mt-1 bg-[#0A1628] border border-[#1E3A5F] text-white text-xs rounded-[3px] px-2 py-1.5 focus:outline-none focus:border-cyan-400"
                    data-testid="submission-distributor-select"
                  >
                    <option value="amazon_kdp">Amazon KDP</option>
                    <option value="ingramspark">IngramSpark</option>
                    <option value="spines">Spines</option>
                    <option value="wiley">Wiley</option>
                    <option value="all">All</option>
                  </select>
                </label>
              </div>

              <button
                onClick={generateSubmission}
                disabled={submGenerating}
                className="w-full md:w-auto px-6 py-3 bg-[#1A6BCC] hover:bg-[#2178DD] text-white text-xs font-bold rounded-[3px] transition-colors disabled:opacity-50"
                data-testid="generate-submission-btn"
              >
                {submGenerating ? "Generating…" : "Generate Submission Package"}
              </button>

              {submResult && !submResult.error && (
                <div className="mt-4 bg-emerald-500/5 border border-emerald-500/30 rounded-[3px] p-3" data-testid="submission-success-banner">
                  <div className="text-xs font-bold text-emerald-300 mb-1">Submission ZIP downloaded</div>
                  <div className="text-[11px] text-slate-300">
                    <b>{submResult.filename}</b> · {(submResult.sizeBytes / 1024 / 1024).toFixed(2)} MB ·
                    ISBN <span className="text-cyan-400">{submResult.isbn}</span> ·
                    {submResult.wordCount} words · {submResult.pageCount} pages · hash <code className="text-slate-500">{submResult.zipHash}</code>
                  </div>
                </div>
              )}
              {submResult?.error && (
                <div className="mt-4 bg-red-500/5 border border-red-500/30 rounded-[3px] p-3 text-xs text-red-300" data-testid="submission-error-banner">
                  Error: {submResult.error}
                </div>
              )}

              {/* Metadata copy-to-clipboard */}
              {submPreview && (
                <div className="mt-6" data-testid="submission-metadata-panel">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] font-bold text-white uppercase tracking-wider">Distributor form fields</div>
                    <div className="text-[10px] text-slate-500">Click any row to copy</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {(() => {
                      const m = submPreview.metadata;
                      const fields = [
                        ["title", "Title", m.title],
                        ["subtitle", "Subtitle", m.subtitle],
                        ["author", "Author", m.author],
                        ["publisher", "Publisher", m.publisher],
                        ["series_title", "Series", m.series_title],
                        ["series_number", "Series #", m.series_number],
                        ["isbn", "ISBN", m.isbn],
                        ["bisac_primary", "BISAC primary", `${m.bisac_primary} — ${m.bisac_primary_heading}`],
                        ["bisac_secondary", "BISAC secondary", `${m.bisac_secondary} — ${m.bisac_secondary_heading}`],
                        ["keywords", "Keywords (7)", m.keywords.join("; ")],
                        ["language", "Language", m.language],
                        ["trim_size", "Trim size", m.trim_size],
                        ["orcid", "ORCID", m.orcid],
                        ["reader_hub_url", "Reader hub URL", m.reader_hub_url],
                      ];
                      return fields.map(([key, label, val]) => (
                        <button
                          key={key}
                          onClick={() => copyToClipboard(val, key)}
                          className={`flex items-start justify-between gap-2 text-left px-2.5 py-2 rounded-[2px] text-[10px] border transition-colors ${copiedField === key ? "bg-emerald-500/15 border-emerald-500/40" : "bg-[#0A1628] hover:bg-[#0d1b33] border-[#1E3A5F]/50 hover:border-cyan-400/30"}`}
                          data-testid={`copy-field-${key}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-slate-500 uppercase tracking-wider text-[9px]">{label}</div>
                            <div className={`mt-0.5 truncate ${copiedField === key ? "text-emerald-300" : "text-slate-200"}`}>{val || "—"}</div>
                          </div>
                          <span className={`text-[9px] font-bold shrink-0 ${copiedField === key ? "text-emerald-300" : "text-slate-500"}`}>
                            {copiedField === key ? "✓ COPIED" : "⧉"}
                          </span>
                        </button>
                      ));
                    })()}
                  </div>

                  {/* Descriptions — wider */}
                  <div className="mt-1.5 space-y-1.5">
                    {[
                      ["description_short", `Short description (${submPreview.metadata.description_short.length} chars)`, submPreview.metadata.description_short],
                      ["description_long", `Long description (${submPreview.metadata.description_long.length} chars)`, submPreview.metadata.description_long],
                    ].map(([key, label, val]) => (
                      <button
                        key={key}
                        onClick={() => copyToClipboard(val, key)}
                        className={`w-full flex items-start justify-between gap-2 text-left px-2.5 py-2 rounded-[2px] text-[10px] border transition-colors ${copiedField === key ? "bg-emerald-500/15 border-emerald-500/40" : "bg-[#0A1628] hover:bg-[#0d1b33] border-[#1E3A5F]/50 hover:border-cyan-400/30"}`}
                        data-testid={`copy-field-${key}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-slate-500 uppercase tracking-wider text-[9px]">{label}</div>
                          <div className={`mt-0.5 line-clamp-3 ${copiedField === key ? "text-emerald-300" : "text-slate-200"}`}>{val}</div>
                        </div>
                        <span className={`text-[9px] font-bold shrink-0 ${copiedField === key ? "text-emerald-300" : "text-slate-500"}`}>
                          {copiedField === key ? "✓ COPIED" : "⧉"}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Checklist preview */}
                  <div className="mt-5" data-testid="checklist-preview">
                    <div className="text-[11px] font-bold text-white uppercase tracking-wider mb-2">
                      {submDistributor.replace(/_/g, " ").toUpperCase()} — first 5 steps
                    </div>
                    <ol className="space-y-1">
                      {submPreview.checklist_preview.map((step, idx) => (
                        <li key={idx} className="flex gap-2 text-[11px] text-slate-300" data-testid={`checklist-step-${idx + 1}`}>
                          <span className="text-cyan-400 font-bold shrink-0">{idx + 1}.</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              )}

              {/* Submission history */}
              {submHistory.length > 0 && (
                <div className="mt-6 border-t border-[#1E3A5F] pt-4" data-testid="submission-history">
                  <div className="text-[11px] font-bold text-white uppercase tracking-wider mb-2">Recent submissions</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className="text-slate-500 uppercase tracking-wider border-b border-[#1E3A5F]">
                          <th className="py-1.5 pr-3 text-left">When</th>
                          <th className="py-1.5 pr-3 text-left">Series</th>
                          <th className="py-1.5 pr-3 text-left">Edition</th>
                          <th className="py-1.5 pr-3 text-left">Distributor</th>
                          <th className="py-1.5 pr-3 text-left">Status</th>
                          <th className="py-1.5 pr-3 text-left">Product URL</th>
                          <th className="py-1.5 pr-3 text-right">Size</th>
                          <th className="py-1.5 pr-3 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {submHistory.map((row) => {
                          const status = row.distribution_status || "generated";
                          const chip = {
                            generated: { bg: "bg-slate-500/15", border: "border-slate-400/40", text: "text-slate-300" },
                            submitted: { bg: "bg-sky-500/15", border: "border-sky-400/40", text: "text-sky-300" },
                            live: { bg: "bg-emerald-500/15", border: "border-emerald-400/40", text: "text-emerald-300" },
                            rejected: { bg: "bg-red-500/15", border: "border-red-400/40", text: "text-red-300" },
                            withdrawn: { bg: "bg-amber-500/15", border: "border-amber-400/40", text: "text-amber-300" },
                          }[status] || { bg: "bg-slate-500/15", border: "border-slate-400/40", text: "text-slate-300" };
                          return (
                            <tr key={row.submission_id} className="text-slate-300 border-b border-[#1E3A5F]/30" data-testid={`history-row-${row.submission_id}`}>
                              <td className="py-1.5 pr-3 whitespace-nowrap">{new Date(row.generated_at).toLocaleString()}</td>
                              <td className="py-1.5 pr-3">{row.series}</td>
                              <td className="py-1.5 pr-3">{row.edition}</td>
                              <td className="py-1.5 pr-3">{row.distributor}</td>
                              <td className="py-1.5 pr-3">
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-[2px] border text-[9px] font-bold uppercase tracking-wider ${chip.bg} ${chip.border} ${chip.text}`}
                                  title={row.notes || ""}
                                  data-testid={`status-badge-${row.submission_id}`}
                                >
                                  {status}
                                </span>
                              </td>
                              <td className="py-1.5 pr-3">
                                {row.distributor_product_url ? (
                                  <a
                                    href={row.distributor_product_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-cyan-400 hover:underline truncate inline-block max-w-[160px]"
                                    data-testid={`product-url-${row.submission_id}`}
                                  >
                                    {row.distributor_product_id || "open"}
                                  </a>
                                ) : <span className="text-slate-500">—</span>}
                              </td>
                              <td className="py-1.5 pr-3 text-right">{(row.zip_size_bytes / 1024 / 1024).toFixed(2)} MB</td>
                              <td className="py-1.5 pr-3 text-right">
                                <button
                                  onClick={() => openMarkModal(row)}
                                  className="px-2 py-0.5 bg-[#0A1628] hover:bg-[#1A6BCC]/30 border border-[#1E3A5F] hover:border-cyan-400/50 text-slate-300 hover:text-cyan-400 text-[9px] font-bold rounded-[2px] transition-colors"
                                  data-testid={`mark-btn-${row.submission_id}`}
                                >
                                  Mark as…
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-white font-bold text-sm">BrainDrain Series Bundle</h2>
                  <p className="text-slate-400 text-xs mt-1">
                    Complete publisher-ready manuscript package with Front Matter Extensions (8a-8h),
                    sample pages, Python computational templates, and visual/table template library.
                  </p>
                </div>
                <button
                  onClick={async () => {
                    setDownloading(true);
                    try {
                      const readyRes = await fetch(`${API}/publishing/bundle/download/ready`, { credentials: "include" });
                      if (readyRes.ok) {
                        const readyData = await readyRes.json();
                        if (!readyData.ready) {
                          alert("Bundle is still building after server restart. Please wait 30 seconds and try again.");
                          setDownloading(false);
                          return;
                        }
                      }
                      window.location.href = `${API}/publishing/bundle/download`;
                    } catch (e) {
                      console.error("Download error:", e);
                    }
                    setTimeout(() => setDownloading(false), 3000);
                  }}
                  disabled={downloading}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#1A6BCC] hover:bg-[#2178DD] text-white text-xs font-bold rounded-[3px] transition-colors disabled:opacity-50 shrink-0"
                  data-testid="download-bundle-btn"
                >
                  <Download className="w-4 h-4" />
                  {downloading ? "Downloading..." : "Download Publisher Package"}
                </button>
              </div>

              {/* Bundle Contents */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
                {[
                  { folder: "01_Outlines", label: "8a-8h Outlines", desc: "Full prose distributed across Vol I-III", count: "8 files" },
                  { folder: "02_Sample_Pages", label: "Sample Pages", desc: "Lifecycle Map + Analytical Toolkit", count: "2 pages" },
                  { folder: "03_Python_Templates", label: "Python Templates", desc: "Cpk, AQL, Weibull, Aging calculators", count: "5 files" },
                  { folder: "04_Template_Library", label: "Template Library", desc: "Visual, table, data dict, caption templates", count: "12 files" },
                ].map((item) => (
                  <div key={item.folder} className="bg-[#0A1628] border border-[#1E3A5F]/50 rounded-[3px] p-4">
                    <div className="text-cyan-400 text-[10px] uppercase tracking-wider font-bold mb-1">{item.folder}</div>
                    <div className="text-white text-xs font-bold mb-1">{item.label}</div>
                    <p className="text-slate-500 text-[10px] mb-2">{item.desc}</p>
                    <div className="text-slate-600 text-[9px]">{item.count}</div>
                  </div>
                ))}
              </div>

              {/* Deliverables Checklist */}
              <div className="mt-6 bg-[#0A1628] border border-[#1E3A5F]/50 rounded-[3px] p-4">
                <div className="text-white text-xs font-bold mb-3 flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-emerald-400" /> Deliverables Checklist
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    "Front Matter Extensions (8a-8h) distributed across Vol I-III",
                    "Standards crosswalks: ISO 13485, FDA QMSR, ISO 14971, ICH Q2/Q7",
                    "Lifecycle frameworks mapped to 6 stages per chapter",
                    "76 AI-generated case studies across 38 chapters",
                    "38 computational charts with threshold lines",
                    "4 Python computational templates with worked examples",
                    "Class III thresholds defined per chapter",
                    "Post-production review & field feedback per chapter",
                    "Cross-volume references and technique atlas",
                    "Editorial memo and package manifest",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                      <span className="text-slate-400">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Domain Focus */}
              <div className="mt-4 flex flex-wrap gap-2">
                {["Medical Devices Class III", "Combination Products", "Pharma", "Industrial", "TPMs", "Component Manufacturers", "Disposables/Single-Use"].map((tag) => (
                  <span key={tag} className="text-[9px] bg-cyan-500/10 text-cyan-400 px-2 py-0.5 rounded font-medium">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* COURSES TAB */}
        {activeTab === "courses" && (
          <div className="space-y-4" data-testid="courses-tab">
            {courses.length === 0 ? (
              <div className="text-center py-16">
                <GraduationCap className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">No courses available yet.</p>
              </div>
            ) : (
              courses.map((course) => (
                <div key={course.course_id} className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid={`course-${course.course_id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-white font-bold text-sm">{course.title}</h3>
                      <p className="text-slate-400 text-xs mt-0.5 max-w-xl">{course.description}</p>
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      <div className="text-[#00C8E0] font-bold text-lg">{course.progress_pct}%</div>
                      <div className="text-slate-500 text-xs">{course.completed_count}/{course.module_count}</div>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-[#0F1E35] rounded-full mb-4">
                    <div className="h-full bg-[#1A6BCC] rounded-full transition-all" style={{ width: `${course.progress_pct}%` }} />
                  </div>
                  <div className="space-y-1">
                    {course.modules?.map((m) => {
                      const completed = course.completed_count > 0 && m.module_num <= course.completed_count;
                      return (
                        <div key={m.module_num} className="flex items-center justify-between p-2 rounded-[3px] hover:bg-[#0F1E35] transition-colors">
                          <div className="flex items-center gap-2">
                            {completed ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <div className="w-4 h-4 rounded-full border border-slate-600" />}
                            <span className={`text-xs ${completed ? "text-slate-400 line-through" : "text-slate-200"}`}>Ch. {m.module_num}: {m.title}</span>
                          </div>
                          {!completed && (
                            <button onClick={() => completeModule(course.course_id, m.module_num)} className="text-xs text-[#00C8E0] hover:text-white px-2 py-1" data-testid={`complete-mod-${course.course_id}-${m.module_num}`}>
                              Mark Complete
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* WHERE TO BUY TAB */}
        {activeTab === "where-to-buy" && (
          <div className="space-y-6" data-testid="where-to-buy">
            <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6">
              <h2 className="text-white font-bold text-sm mb-2">Purchase Full Books</h2>
              <p className="text-slate-400 text-xs mb-4">
                All three books in the Brain Drain Series are available as e-Books and Hardcover editions (I, II, and III). ISBNs have been assigned via Global Publishing Network. Marketplace listings will be updated as distribution channels go live.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="marketplace-grid">
                {Object.entries(marketplaces).map(([key, mp]) => (
                  <div key={key} className="bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px] p-4 flex items-center gap-3" data-testid={`marketplace-${key}`}>
                    <Globe className="w-5 h-5 text-[#00C8E0] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-bold">{mp.name}</div>
                      <div className="text-xs text-amber-400 uppercase font-bold mt-0.5">
                        {mp.status === "coming_soon" ? "Coming Soon" : "Available"}
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-600" />
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6" data-testid="publishing-roadmap">
              <h2 className="text-white font-bold text-sm mb-1 flex items-center gap-2">
                <Map className="w-4 h-4 text-[#00C8E0]" /> Publishing Roadmap
              </h2>
              <p className="text-slate-400 text-xs mb-4">Distribution plan for external platforms. ISBNs assigned — ready for marketplace registration.</p>
              <div className="space-y-3">
                {publishingRoadmap.map((p, i) => (
                  <div key={i} className="bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px] p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-white font-bold text-sm">{p.platform}</h3>
                      <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-[#00C8E0] hover:text-white text-xs flex items-center gap-1">
                        Visit <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <p className="text-slate-400 text-xs mb-2">{p.steps}</p>
                    <div className="text-xs text-slate-500">Formats: {p.formats}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

export default PublishingPortalPage;
